/**
 * Catalogue.jsx — public marketing page surfacing the connector catalogue.
 *
 * Mounted at `/catalogue`, unauthenticated. Lets a prospective customer
 * see exactly what ERIO talks to — free connectors first, then premium
 * with a "Customer licence required" pill — and ends in a single
 * "Book a pilot" CTA. Reuses the public catalogue endpoint and matches
 * the visual language of the ROI calculator and Trust Centre.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plug, ExternalLink, CheckCircle2, Sparkles, Loader2, ArrowRight, Filter, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Navbar } from "@/components/Navbar";
import { LeadForm } from "@/components/LeadForm";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";

export default function Catalogue() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState({ free_count: 0, premium_count: 0 });
  const [err, setErr] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    api
      .get("/public/connectors")
      .then(({ data }) => {
        setItems(data.items || []);
        setSummary({
          free_count: data.free_count || 0,
          premium_count: data.premium_count || 0,
        });
      })
      .catch(() => setErr(t("catalogue.errorLoad")));
  }, [t]);

  // Distinct categories — sorted alphabetically with the most-populated first.
  const categories = useMemo(() => {
    if (!items) return [];
    const counts = new Map();
    items.forEach((c) => {
      counts.set(c.category, (counts.get(c.category) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (activeCategory === "all") return items;
    return items.filter((c) => c.category === activeCategory);
  }, [items, activeCategory]);

  const free = filteredItems.filter((c) => !c.is_premium);
  const premium = filteredItems.filter((c) => c.is_premium);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo
        title={t("catalogue.seoTitle")}
        description={t("catalogue.seoDescription")}
        path="/catalogue"
        localized
      />
      <Navbar />

      <main className="mx-auto max-w-6xl px-6 py-12">
        {/* ---------- Hero ---------- */}
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-ek-purple/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ek-purple"
             data-testid="catalogue-eyebrow">
            <Plug className="h-3.5 w-3.5" /> {t("catalogue.eyebrow")}
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold text-ek-ink sm:text-5xl">
            {t("catalogue.title")}
          </h1>
          <p className="mt-4 text-base text-ek-ink/75 sm:text-lg">
            {t("catalogue.blurbPart1")} <span className="font-semibold">{t("catalogue.blurbFree")}</span> {t("catalogue.blurbPart2")} <span className="font-semibold">{t("catalogue.blurbPremium")}</span> {t("catalogue.blurbPart3")}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/pilot"
              className="inline-flex items-center gap-2 rounded-full bg-ek-purple px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-ek-purple/90"
              data-testid="catalogue-pilot-cta-top"
            >
              {t("catalogue.bookPilot")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-semibold text-ek-ink hover:bg-ek-purple/5"
              data-testid="catalogue-howitworks-cta"
            >
              {t("catalogue.howItWorks")}
            </Link>
            <LocaleSwitcher tone="light" />
          </div>
        </div>

        {/* ---------- Summary strip ---------- */}
        {items && (
          <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="catalogue-summary">
            <SummaryTile
              label={t("catalogue.summaryFreeLabel")}
              value={summary.free_count}
              accent="text-emerald-700"
              blurb={t("catalogue.summaryFreeBlurb")}
            />
            <SummaryTile
              label={t("catalogue.summaryPremiumLabel")}
              value={summary.premium_count}
              accent="text-ek-purple"
              blurb={t("catalogue.summaryPremiumBlurb")}
            />
            <SummaryTile
              label={t("catalogue.summaryOwnerLabel")}
              value={t("catalogue.summaryOwnerValue")}
              accent="text-amber-700"
              blurb={t("catalogue.summaryOwnerBlurb")}
            />
          </div>
        )}

        {err && (
          <p className="mt-8 text-sm text-red-600" data-testid="catalogue-error">
            {err}
          </p>
        )}

        {!items && !err && (
          <p className="mt-12 inline-flex items-center gap-2 text-sm text-ek-ink/60">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("catalogue.loading")}
          </p>
        )}

        {/* ---------- Category filter chips ---------- */}
        {items && categories.length > 0 && (
          <div className="mt-12" data-testid="catalogue-category-filter">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
              <Filter className="h-3.5 w-3.5" /> {t("catalogue.filterBy")}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCategory("all")}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  activeCategory === "all"
                    ? "bg-ek-purple text-white"
                    : "bg-white text-ek-ink/65 ring-1 ring-black/10 hover:bg-ek-purple/5"
                }`}
                data-testid="catalogue-category-chip-all"
              >
                {t("catalogue.filterAll")} <span className="opacity-70">· {items.length}</span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  onClick={() => setActiveCategory(cat.name)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    activeCategory === cat.name
                      ? "bg-ek-purple text-white"
                      : "bg-white text-ek-ink/65 ring-1 ring-black/10 hover:bg-ek-purple/5"
                  }`}
                  data-testid={`catalogue-category-chip-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {cat.name} <span className="opacity-70">· {cat.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Free connectors ---------- */}
        {items && (
          <Section
            title={t("catalogue.freeTitle")}
            subtitle={t("catalogue.freeBlurb")}
            icon={<CheckCircle2 className="h-4 w-4 text-emerald-700" />}
            items={free}
            testId="catalogue-free"
          />
        )}

        {/* ---------- Premium connectors ---------- */}
        {items && (
          <Section
            title={t("catalogue.premiumTitle")}
            subtitle={t("catalogue.premiumBlurb")}
            icon={<Sparkles className="h-4 w-4 text-ek-purple" />}
            items={premium}
            testId="catalogue-premium"
            premium
          />
        )}

        {/* ---------- Empty state when filter yields nothing ---------- */}
        {items && filteredItems.length === 0 && activeCategory !== "all" && (
          <p
            className="mt-10 rounded-2xl border border-dashed border-black/10 bg-white p-6 text-sm text-ek-ink/65"
            data-testid="catalogue-empty-category"
          >
            {t("catalogue.emptyCategoryPart1")} <span className="font-semibold text-ek-ink">{activeCategory}</span>.
            {" "}{t("catalogue.emptyCategoryPart2")}
          </p>
        )}

        {/* ---------- Vendor-watcher lead capture ---------- */}
        {items && (
          <section
            className="mt-16 rounded-2xl border border-black/5 bg-white p-6 sm:p-8"
            data-testid="catalogue-watcher-section"
          >
            <div className="flex items-start gap-3">
              <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-ek-purple/10 text-ek-purple">
                <Bell className="h-4 w-4" />
              </span>
              <div>
                <h2 className="font-display text-xl font-semibold text-ek-ink">
                  {t("catalogue.watcherTitle")}
                </h2>
                <p className="mt-1 text-sm text-ek-ink/70">
                  {t("catalogue.watcherBlurb")}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <LeadForm
                kind="brochure"
                source="catalogue_watcher"
                showMessage
                messagePlaceholder={t("catalogue.watcherPlaceholder")}
                appendMessage="Came from the public /catalogue page — connector watcher signup."
              />
            </div>
          </section>
        )}

        {/* ---------- Bottom CTA ---------- */}
        {items && (
          <div className="mt-16 rounded-2xl border border-black/5 bg-gradient-to-br from-ek-purple/10 to-white p-8 sm:flex sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-display text-xl font-semibold text-ek-ink">
                {t("catalogue.bottomTitle")}
              </h2>
              <p className="mt-1 text-sm text-ek-ink/70">
                {t("catalogue.bottomBlurb")}
              </p>
            </div>
            <Link
              to="/pilot"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-ek-purple px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-ek-purple/90 sm:mt-0"
              data-testid="catalogue-pilot-cta-bottom"
            >
              {t("catalogue.bottomCta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}


function SummaryTile({ label, value, accent, blurb }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5">
      <p className={`font-display text-3xl font-semibold ${accent}`}>{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-ek-ink/60">{label}</p>
      <p className="mt-2 text-xs text-ek-ink/65">{blurb}</p>
    </div>
  );
}


function Section({ title, subtitle, icon, items, testId, premium }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <section className="mt-12" data-testid={testId}>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-display text-xl font-semibold text-ek-ink">{title}</h2>
        <span className="ml-1 rounded-full bg-ek-ink/5 px-2 py-0.5 text-[11px] font-semibold text-ek-ink/65">
          {items.length}
        </span>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-ek-ink/65">{subtitle}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <article
            key={c.code}
            className="flex flex-col rounded-xl border border-black/5 bg-white p-4 transition hover:border-ek-purple/30 hover:shadow-sm"
            data-testid={`${testId}-card-${c.code}`}
          >
            <header className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-base font-semibold text-ek-ink">{c.name}</h3>
                <p className="mt-0.5 text-xs text-ek-ink/55">{c.category}</p>
              </div>
              {premium && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                  {t("catalogue.cardLicence")}
                </span>
              )}
            </header>
            {c.demand_count && (
              <p
                className="mt-2 inline-flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-semibold text-ek-purple"
                data-testid={`${testId}-demand-${c.code}`}
                title="Prospects who asked for this connector via the watcher form"
              >
                <span aria-hidden="true">●</span>
                {c.demand_count} {t("catalogue.cardProspectsAsked")}
              </p>
            )}
            <p className="mt-3 flex-1 text-xs text-ek-ink/65">
              <span className="font-semibold text-ek-ink">{t("catalogue.cardVendor")}</span> {c.vendor}
              <br />
              <span className="font-semibold text-ek-ink">{t("catalogue.cardCoverage")}</span> {c.region}
            </p>
            {c.licence_url && (
              <a
                href={c.licence_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-ek-purple hover:underline"
              >
                {t("catalogue.cardVendorInfo")} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
